import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QPointConfirmDialogButtonPreset, QPointConfirmDialogService } from '@qpoint/layout';
import { ButtonVariant, QPointButtonComponent } from '@qpoint/buttons';
import {
  DropDownVariant,
  QPointReactiveFormInputGroupComponent
} from '@qpoint/forms';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { injectElementRef } from '@qpoint/utils';
import { WindowComponent, WindowModule } from '@progress/kendo-angular-dialog';
import * as L from 'leaflet';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { QPointNumberFormatPipe } from '@qpoint/i18n';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs';

export type ScaleDialogData = {
  referenceLength: number;
  realLength: number;
}

export type ScaleDialogPosition = {
  top: number;
  left: number;
}

export type ScaleDialogTextConfig = {
  title: string;
  referenceLengthButton: string;
  referenceLengthLabel: string;
  realLengthLabel: string;
  cancelButton: string;
  confirmButton: string;
  referenceLengthUnit: string;
  realLengthUnit: string;
}

@Component({
    selector: 'qpoint-scale-dialog',
    imports: [
        CommonModule,
        QPointButtonComponent,
        QPointReactiveFormInputGroupComponent,
        ReactiveFormsModule,
        WindowModule,
        InputsModule
    ],
    templateUrl: './scale-dialog.component.html',
    styleUrls: ['./scale-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [QPointNumberFormatPipe]
})
export class ScaleDialogComponent {
  protected readonly DropDownVariant = DropDownVariant;
  protected readonly ButtonVariant = ButtonVariant;
  private fb = inject(FormBuilder);
  private numberFormatPipe = inject(QPointNumberFormatPipe);
  private providedData: ScaleDialogData | undefined = undefined;
  protected readonly formId = crypto.randomUUID();
  elementRef = injectElementRef();

  private _display = signal(false);
  display = this._display.asReadonly();

  private _measuring = signal(false);
  measuring = this._measuring.asReadonly();

  private _scaleButtonVariant = signal(ButtonVariant.primary);
  public deleteScaleEditMode = signal(false);
  scaleButtonVariant = this._scaleButtonVariant.asReadonly();

  left = 0;
  top = 0;

  form = this.fb.group({
    referenceLength: this.fb.control(null),
    realLength: this.fb.control(null, [Validators.required]),
  });

  scaleText = toSignal(this.form.valueChanges
    .pipe(
      startWith(this.form.value),
      map((value: ScaleDialogData) => {
        if (value.referenceLength && value.realLength) {
          return `${value.referenceLength.toFixed()} ${this.textConfig.referenceLengthUnit} = ${this.numberFormatPipe.transform(value.realLength, 'n2')} ${this.textConfig.realLengthUnit}`;
        }
      }),
      takeUntilDestroyed(),
    ));

  @Input() set data(data: ScaleDialogData) {
    if (data) {
      this.providedData = data;
      this.form.patchValue(data);
      this._scaleButtonVariant.set(ButtonVariant.secondary);
    } else {
      this.providedData = undefined;
    }
  }

  @Input({required: true}) textConfig: ScaleDialogTextConfig;
  @Output() measureScaleClicked = new EventEmitter();
  @Output() cancelClicked = new EventEmitter();
  @Output() submitMeasurements = new EventEmitter<ScaleDialogData>();

  @ViewChild(WindowComponent) windowComponent: WindowComponent;

  constructor(private confirmService: QPointConfirmDialogService) {
    this.form.get('referenceLength').valueChanges
      .pipe(
        startWith(this.form.get('referenceLength').value),
        map(v => v ? this.ButtonVariant.secondary : this.ButtonVariant.primary),
        takeUntilDestroyed()
      ).subscribe(
      {next: variant => this._scaleButtonVariant.set(variant)}
    );
  }

  public async deleteScale() {
    this.deleteScaleEditMode.set(true);
    const dialogButtons = [...QPointConfirmDialogButtonPreset.default];
    dialogButtons[0].text = 'global.action.delete';
    dialogButtons[1].text = 'global.action.cancel';
    const result = await this.confirmService.openConfirm({
      confirmDialogType: 'warning',
      buttons: dialogButtons,
      message: 'asphaltOperationLot.geometry.deleteScale'
    }).valueOnlyResult as boolean;
    if (result) {
      this.form.reset(null);
      this.handleSubmit();
      this.handleClose();
    }
    this.deleteScaleEditMode.set(false);
  }
  handleSubmit() {
    this.submitMeasurements.emit(this.form.value as ScaleDialogData);
  }

  measureScale() {
    this.measureScaleClicked.emit();
  }

  setDisplay(display: boolean) {
    this._display.set(display);
  }

  setMeasuring(measuring: boolean) {
    this._measuring.set(measuring);
  }

  setPosition(left: number, top: number) {
    this.left = left;
    this.top = top;
    this.windowComponent.setOffset('top', this.top);
    this.windowComponent.setOffset('left', this.left);
  }

  calculateAndSetEuclideanDistance(latLngs: L.LatLng[]) {
    this.setDisplay(true);
    if (latLngs.length !== 2) {
      console.error('[ScaleDialog] invalid length of provided latLngs: exactly 2 points are needed');
      return;
    }

    const eucleanDistance = Math.sqrt(Math.pow(latLngs[1].lat - latLngs[0].lat, 2) + Math.pow(latLngs[1].lng - latLngs[0].lng, 2));

    this.form.patchValue({
      referenceLength: eucleanDistance
    });
  }

  handleCancel() {
    this.setDisplay(false);
    this.form.reset(this.providedData ?? null);
    if(!this.form.get('referenceLength').value) {
      this._scaleButtonVariant.set(ButtonVariant.primary);
    }
    this.cancelClicked.emit();
  }

  handleClose() {
    if (this.measuring()) {
      this.setDisplay(false);
    } else {
      this.handleCancel();
    }
  }

  resetForm() {
    this.form.reset(this.providedData ?? null);
  }
}
